import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/Store';
import {
	CurrencyRestrictions,
	CurrencyRestrictionsActionsEnum,
} from '../redux/CurrencyRestrictionsState/CurrencyRestrictionsTypes';

const useCurrencyRestrictions = () => {
	const currencyRestrictions = useSelector(
		(state: RootState) => state?.CurrencyRestrictionsState?.currencyRestrictions
	);

	const getCurrencyRestrictions = useCallback(
		(currencyCode: string): CurrencyRestrictions | undefined => {
			return currencyRestrictions?.find(
				(item: CurrencyRestrictions) => item.currencyCode === currencyCode
			);
		},
		[currencyRestrictions]
	);

	const getIsActionAllowedForCurrency = useCallback(
		(currencyCode: string, action: CurrencyRestrictionsActionsEnum): boolean => {
			const restrictions = getCurrencyRestrictions(currencyCode);
			return !restrictions?.isRestricted || restrictions?.enabledActions?.includes(action);
		},
		[getCurrencyRestrictions]
	);

	const getAreActionsAllowedForCurrency = useCallback(
		(currencyCode: string, actions: CurrencyRestrictionsActionsEnum[]): boolean => {
			const restrictions = getCurrencyRestrictions(currencyCode);
			return (
				!restrictions?.isRestricted ||
				actions.some((action) => restrictions?.enabledActions?.includes(action))
			);
		},
		[getCurrencyRestrictions]
	);

	const getAreAllActionsAllowedForCurrency = useCallback(
		(currencyCode: string, actions: CurrencyRestrictionsActionsEnum[]): boolean => {
			const restrictions = getCurrencyRestrictions(currencyCode);
			return (
				!restrictions?.isRestricted ||
				actions.every((action) => restrictions?.enabledActions?.includes(action))
			);
		},
		[getCurrencyRestrictions]
	);

	const getAnyWalletActionAllowedForCurrency = useCallback(
		(currencyCode: string): boolean => {
			const restrictions = getCurrencyRestrictions(currencyCode);
			return (
				!restrictions?.isRestricted ||
				restrictions?.enabledActions.includes(CurrencyRestrictionsActionsEnum.DEPOSIT) ||
				restrictions?.enabledActions.includes(CurrencyRestrictionsActionsEnum.EXCHANGE) ||
				restrictions?.enabledActions.includes(CurrencyRestrictionsActionsEnum.WITHDRAWAL)
			);
		},
		[getCurrencyRestrictions]
	);

	return {
		getIsActionAllowedForCurrency,
		getAreActionsAllowedForCurrency,
		getAreAllActionsAllowedForCurrency,
		getAnyWalletActionAllowedForCurrency,
	};
};

export default useCurrencyRestrictions;
